import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Field, Form, Formik, FormikConfig } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useContext } from 'react';

import { Button } from 'common/components/Button';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { OfferFormFields } from 'offer/forms/OfferFormFields';
import {
    HEADER_IMAGE_MAX_SIZE,
    HEADER_IMAGE_TYPE,
} from 'common/constants/files';
import { IMAGE_VARIANTS } from 'common/constants/image';
import { EImageVariant } from 'common/types';
import { ImageField } from 'common/components/FormField';
import {
    IFileFragment,
    IOfferDetailFragment,
    IOfferEventType,
} from 'graphql/types';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import { TUpdateOfferFormInput } from 'offer/types';
import { OfferPermissionsContext } from 'offer/contexts';

interface IProps extends FormikConfig<TUpdateOfferFormInput> {
    disabled?: boolean;
    imageUploaded?: boolean;
    isDeleting?: boolean;
    offer: IOfferDetailFragment;
    onChange?(name: string, file: IFileFragment): void;
    onClose?(): void;
    onDelete?(): void;
}

export const UpdateOfferForm = ({
    disabled,
    imageUploaded,
    isDeleting,
    offer,
    onChange,
    onClose,
    onDelete,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const { canDelete = false } = useContext(OfferPermissionsContext) || {};

    const validationSchema = Yup.object().shape({
        title: Yup.string().required(translate('validate.required')),
        description: Yup.string().optional(),
    });

    const isWebinarOrMeeting =
        offer.types.includes(IOfferEventType.Webinar) ||
        offer.types.includes(IOfferEventType.Meeting);

    return (
        <Formik
            enableReinitialize
            validationSchema={validationSchema}
            {...other}
        >
            {({ submitForm, isSubmitting, dirty }) => (
                <Box sx={{ p: 4 }}>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={
                            (dirty || imageUploaded) &&
                            !isSubmitting &&
                            !isDeleting
                        }
                        onConfirm={submitForm}
                    />

                    <Form>
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                sx={{ fontWeight: 'bold' }}
                                variant="h3"
                            >
                                {translate('offer.updateOfferTitle')}
                            </Typography>

                            <Box sx={{ mt: 2 }}>
                                <Typography>
                                    {translate('offer.updateOfferDescription')}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ mb: 1 }}>
                            <Field
                                withDefaults
                                acceptedFileTypes={HEADER_IMAGE_TYPE}
                                component={ImageField}
                                helperText={translate('imageSizeDescription', {
                                    width: IMAGE_VARIANTS[EImageVariant.Header]
                                        .minWidth,
                                    height: IMAGE_VARIANTS[EImageVariant.Header]
                                        .minHeight,
                                })}
                                imageVariant={EImageVariant.Header}
                                label={translate('headerImage')}
                                maxUploadSize={HEADER_IMAGE_MAX_SIZE}
                                name="image"
                            />
                        </Box>

                        <OfferFormFields
                            renderExtraCategoryFields
                            renderGroupConditionFields
                            isWebinarOrMeeting={isWebinarOrMeeting}
                            renderTypeSelect={!!offer.type}
                        />

                        <Box sx={{ display: 'flex', mt: 2 }}>
                            <Button
                                color="primary"
                                disabled={
                                    isSubmitting ||
                                    disabled ||
                                    (!isSubmitting &&
                                        !disabled &&
                                        !dirty &&
                                        !imageUploaded)
                                }
                                loading={isSubmitting}
                                type="submit"
                                variant="contained"
                                onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>
                                ) => {
                                    e.preventDefault();

                                    submitForm();
                                }}
                            >
                                {translate('save')}
                            </Button>

                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}

                            {canDelete && !!onDelete && (
                                <Box sx={{ ml: 'auto' }}>
                                    <FormDeleteButton
                                        disabled={isSubmitting}
                                        onClick={onDelete}
                                    >
                                        {translate('delete')}
                                    </FormDeleteButton>
                                </Box>
                            )}
                        </Box>
                    </Form>
                </Box>
            )}
        </Formik>
    );
};
